// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import { formatHumanName, getReferenceString } from '@medplum/core';
import type { Extension, Practitioner, PractitionerRole, Resource, Schedule } from '@medplum/fhirtypes';
import type { AppointmentTypeDefinition, ProviderRoleCode } from './appointment-types';
import { APPOINTMENT_TYPE_SYSTEM, POLARIS_PROVIDER_ROLE_SYSTEM } from './appointment-types';
import {
  getScheduleTimezone,
  PRACTITIONER_TIMEZONE_EXTENSION_URL,
  reconcileScheduleAppointmentTypeAvailability,
} from './schedule-availability';
import type { AvailableTimeSlot } from './types';

/** Default minimum notice in hours before a slot can be booked */
const DEFAULT_MINIMUM_NOTICE_HOURS = 0;

interface EligibleProvider {
  practitioner: Practitioner;
  practitionerRole: PractitionerRole;
  roleCode: ProviderRoleCode;
}

/**
 * Finds practitioners eligible to handle a given appointment type based on their role.
 * Uses _include to fetch Practitioner resources in the same search (avoids N+1 reads).
 * If the appointment type has a preferred provider, preferred providers are sorted first.
 *
 * @param medplum - The Medplum client
 * @param appointmentType - The appointment type definition
 * @returns Array of eligible providers, sorted with preferred providers first
 */
export async function findEligibleProviders(
  medplum: MedplumClient,
  appointmentType: AppointmentTypeDefinition
): Promise<EligibleProvider[]> {
  const providers: EligibleProvider[] = [];

  for (const roleCode of appointmentType.providerTypes) {
    // _include fetches referenced Practitioner resources in the same response
    const bundle = await medplum.search('PractitionerRole', [
      ['role', `${POLARIS_PROVIDER_ROLE_SYSTEM}|${roleCode}`],
      ['_include', 'PractitionerRole:practitioner'],
      ['_count', '100'],
    ]);

    // Separate PractitionerRoles from included Practitioners
    const practitionerMap = new Map<string, Practitioner>();
    const practitionerRoles: PractitionerRole[] = [];

    for (const entry of bundle.entry ?? []) {
      const resource = entry.resource as Resource | undefined;
      if (!resource) {
        continue;
      }
      // _include returns both PractitionerRole and Practitioner in the same bundle
      if (resource.resourceType === 'Practitioner' && resource.id) {
        practitionerMap.set(`Practitioner/${resource.id}`, resource as Practitioner);
      } else if (resource.resourceType === 'PractitionerRole') {
        practitionerRoles.push(resource as PractitionerRole);
      }
    }

    for (const pr of practitionerRoles) {
      if (!pr.practitioner?.reference) {
        continue;
      }

      // Avoid duplicates if a practitioner has multiple roles
      if (providers.some((p) => getReferenceString(p.practitioner) === pr.practitioner?.reference)) {
        continue;
      }

      const practitioner = practitionerMap.get(pr.practitioner.reference);
      if (practitioner) {
        providers.push({ practitioner, practitionerRole: pr, roleCode });
      }
    }
  }

  // Sort preferred providers first
  if (appointmentType.preferredProvider) {
    const preferred = appointmentType.preferredProvider;
    providers.sort((a, b) => {
      if (a.roleCode === preferred && b.roleCode !== preferred) {
        return -1;
      }
      if (a.roleCode !== preferred && b.roleCode === preferred) {
        return 1;
      }
      return 0;
    });
  }

  return providers;
}

interface FindAvailableSlotsParams {
  /** Practitioner IDs to search schedules for */
  practitionerIds: string[];
  /** Start of the date range (ISO datetime) */
  startDate: string;
  /** End of the date range (ISO datetime) */
  endDate: string;
  /** The appointment type (used for duration filtering) */
  appointmentType: AppointmentTypeDefinition;
  /** Minimum notice in hours before a slot can be booked (default: 0) */
  minimumNoticeHours?: number;
  /** Pre-resolved provider display names keyed by practitioner ID (avoids redundant reads) */
  providerNames?: Record<string, string>;
  /** Rebuild missing Slots from provider availability templates for booking flows. */
  reconcileMissingSlots?: boolean;
}

/**
 * Finds available time slots for the given practitioners within a date range.
 * Batches Schedule and Slot queries to avoid N+1 per-practitioner fetches.
 * Filters slots by status, appointment type, date range, and minimum notice.
 *
 * @param medplum - The Medplum client
 * @param params - Search parameters
 * @returns Array of available time slots sorted by start time
 */
export async function findAvailableSlots(
  medplum: MedplumClient,
  params: FindAvailableSlotsParams
): Promise<AvailableTimeSlot[]> {
  const {
    practitionerIds,
    startDate,
    endDate,
    appointmentType,
    minimumNoticeHours = DEFAULT_MINIMUM_NOTICE_HOURS,
    providerNames = {},
    reconcileMissingSlots = false,
  } = params;

  if (practitionerIds.length === 0) {
    return [];
  }

  const minimumNoticeMs = minimumNoticeHours * 60 * 60 * 1000;
  const now = new Date();
  const earliestBookable = new Date(now.getTime() + minimumNoticeMs);

  // Step 1: Fetch all schedules for all practitioners in one query
  const actorList = practitionerIds.map((id) => `Practitioner/${id}`).join(',');
  const schedules = await medplum.searchResources('Schedule', [
    ['actor', actorList],
    ['active', 'true'],
    ['_count', '100'],
  ]);

  if (schedules.length === 0) {
    return [];
  }

  // Build schedule → practitioner mapping
  const scheduleToProvider = new Map<string, string>();
  for (const schedule of schedules) {
    if (!schedule.id) {
      continue;
    }
    const actorRef = (schedule as Schedule).actor?.[0]?.reference;
    if (actorRef?.startsWith('Practitioner/')) {
      scheduleToProvider.set(`Schedule/${schedule.id}`, actorRef.replace('Practitioner/', ''));
    }
  }

  // Availability lookup is read-only. Slot reconciliation is performed by the
  // explicit availability-management flows, not while rendering scheduling UI.
  // Reconciliation here would delete and recreate slots on every availability
  // request and can trigger Medplum rate limits under concurrent scheduling views.
  // Step 2: Fetch all free slots for all schedules in one query
  const scheduleRefs = schedules
    .filter((s) => s.id)
    .map((s) => getReferenceString(s))
    .join(',');

  const slotSearch = [
    ['schedule', scheduleRefs],
    ['status', 'free'],
    ['service-type', `${APPOINTMENT_TYPE_SYSTEM}|${appointmentType.coding.code}`],
    ['start', `ge${startDate}`],
    ['start', `le${endDate}`],
    ['_count', '1000'],
  ] as [string, string][];
  let slots = await medplum.searchResources('Slot', slotSearch);

  // Older schedules may have an availability template but no materialized Slot resources.
  // Keep normal availability reads side-effect free; intake search opts into this bounded repair
  // because it is the only public path that must work for an existing provider calendar immediately.
  if (reconcileMissingSlots && slots.length === 0) {
    await Promise.all(
      schedules.map(async (schedule) => {
        const actorRef = schedule.actor?.[0]?.reference;
        const practitionerId = actorRef?.startsWith('Practitioner/')
          ? actorRef.replace('Practitioner/', '')
          : undefined;
        if (!practitionerId || !schedule.id) {
          return;
        }

        const practitioner = (await medplum.readResource('Practitioner', practitionerId)) as Practitioner;
        const timezone = practitioner.extension?.find(
          (extension: Extension) => extension.url === PRACTITIONER_TIMEZONE_EXTENSION_URL
        )?.valueString;

        await reconcileScheduleAppointmentTypeAvailability(medplum, {
          schedule,
          timezone: getScheduleTimezone(timezone),
          startDate,
          endDate,
          appointmentType,
        });
      })
    );
    slots = await medplum.searchResources('Slot', slotSearch);
  }

  // Step 3: Map slots to AvailableTimeSlot with provider info
  const allSlots: AvailableTimeSlot[] = [];

  for (const slot of slots) {
    if (!slot.id || !slot.start || !slot.end || !slot.schedule?.reference) {
      continue;
    }

    const slotStart = new Date(slot.start);

    // Filter: must be after minimum notice period
    if (slotStart < earliestBookable) {
      continue;
    }

    const practitionerId = scheduleToProvider.get(slot.schedule.reference) ?? '';
    const providerName = providerNames[practitionerId] ?? 'Provider';

    // Compute patient-visible end time (exclude cooldown buffer)
    const appointmentEnd = new Date(slotStart.getTime() + appointmentType.durationMinutes * 60 * 1000);

    allSlots.push({
      slotId: slot.id,
      providerId: practitionerId,
      providerName,
      start: slot.start,
      end: appointmentEnd.toISOString(),
      scheduleId: slot.schedule.reference.replace('Schedule/', ''),
    });
  }

  // Sort by start time, preserving practitioner order (from utilization ranking) for same-time slots
  allSlots.sort((a, b) => {
    const timeDiff = new Date(a.start).getTime() - new Date(b.start).getTime();
    if (timeDiff !== 0) {
      return timeDiff;
    }
    // Same start time: preserve practitioner ranking order
    return practitionerIds.indexOf(a.providerId) - practitionerIds.indexOf(b.providerId);
  });

  return allSlots;
}

/**
 * Ranks practitioners by utilization (fewest existing appointments = lowest utilization).
 * Used when multiple providers have overlapping available slots to prefer the less busy provider.
 *
 * @param medplum - The Medplum client
 * @param practitionerIds - IDs of practitioners to rank
 * @param startDate - Start of date range for counting appointments
 * @param endDate - End of date range for counting appointments
 * @returns Practitioner IDs sorted by ascending appointment count
 */
export async function rankProvidersByUtilization(
  medplum: MedplumClient,
  practitionerIds: string[],
  startDate: string,
  endDate: string
): Promise<string[]> {
  if (practitionerIds.length === 0) {
    return [];
  }

  const actorList = practitionerIds.map((id) => `Practitioner/${id}`).join(',');
  const appointments = await medplum.searchResources('Appointment', [
    ['actor', actorList],
    ['date', `ge${startDate}`],
    ['date', `le${endDate}`],
    ['status:not', 'cancelled'],
    ['_count', '1000'],
  ]);

  const countMap = new Map<string, number>(practitionerIds.map((id) => [id, 0]));
  for (const appointment of appointments) {
    for (const participant of appointment.participant ?? []) {
      const actorRef = participant.actor?.reference;
      if (!actorRef?.startsWith('Practitioner/')) {
        continue;
      }

      const practitionerId = actorRef.replace('Practitioner/', '');
      if (!countMap.has(practitionerId)) {
        continue;
      }

      countMap.set(practitionerId, (countMap.get(practitionerId) ?? 0) + 1);
    }
  }

  const counts = practitionerIds.map((id) => ({ id, count: countMap.get(id) ?? 0 }));

  counts.sort((a, b) => a.count - b.count);
  return counts.map((c) => c.id);
}
